import { BookUser } from "lucide-react";
import { EmptyState } from "@/components/shared/empty-state";
import type { BookAccess } from "@/lib/profitability-api";

/**
 * The engine's empty-book reasons, in words a Client Partner can act on.
 * Matched on the start of the sentence so a reworded tail still lands; an
 * unknown reason is shown as the engine wrote it rather than hidden.
 */
const REASONS: { startsWith: string; text: string }[] = [
  {
    startsWith: "No NOS person matches",
    text: "We couldn't match your sign-in to a person in NOS — ask Zack to check your email in NOS.",
  },
  {
    startsWith: "No accounts are recorded for you",
    text: "NOS doesn't list you on any account yet — ask Zack to add you to your accounts in NOS.",
  },
  {
    startsWith: "NOS could not be read",
    text: "We couldn't read NOS just now, so we can't tell which accounts are yours. Try again in a few minutes.",
  },
  {
    startsWith: "NOS is not configured",
    text: "We can't tell which accounts are yours right now. Tell Zack — NOS isn't connected.",
  },
];

export function bookReasonText(reason: string): string {
  return REASONS.find((r) => reason.startsWith(r.startsWith))?.text ?? reason;
}

/** True when an Aggregate response came back with nothing in the book. */
export function isEmptyBook(access: BookAccess | undefined): access is BookAccess {
  return access !== undefined && (access.book_accounts === 0 || Boolean(access.reason));
}

/** The empty-book state, in place of a grid that would only say "no rows". */
export function EmptyBook({ access }: { access: BookAccess }) {
  return (
    <EmptyState
      icon={BookUser}
      title="No accounts to show yet"
      description={
        access.reason
          ? bookReasonText(access.reason)
          : "NOS doesn't list you on any account yet — ask Zack to add you to your accounts in NOS."
      }
    />
  );
}

/**
 * "Your book · 18 accounts with activity in this range" -- so a Client Partner
 * reads the list as their book, not the company's, and the number matches the
 * rows the range puts on screen. Renders nothing for Full and Admin users,
 * whose responses carry no `access`, or until the count is known.
 *
 * Not `access.book_accounts`: that counts every account in their NOS book,
 * dormant ones included, and read as a bug above a shorter list.
 */
export function YourBook({
  access,
  count,
  noun,
  scope = "with activity in this range",
}: {
  access: BookAccess | undefined;
  count: number | undefined;
  /** Singular and plural, e.g. ["account", "accounts"]. */
  noun: [string, string];
  /** What the count is of. At Risk counts its own rows, not the whole range. */
  scope?: string;
}) {
  if (!access || count === undefined) return null;
  return (
    <p className="flex items-center gap-1.5 text-sm text-muted-foreground">
      <BookUser className="h-4 w-4" aria-hidden />
      Your book · {count.toLocaleString()} {count === 1 ? noun[0] : noun[1]} {scope}
    </p>
  );
}
